import { AsyncPipe, NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  effect,
  inject,
  input,
  output,
  signal,
  viewChild,
  ViewEncapsulation,
} from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { SearchService } from '@core/services';
import { OiqFormInputComponent } from '@oiq-ui-sdk/form';
import { OiqUiPanelComponent } from '@oiq-ui-sdk/ui';
import { OiqClientFundingHistoryTable } from '@v2/modules/clients/components/funding-history/funding-history.table';
import { of } from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  map,
  switchMap,
  tap,
} from 'rxjs/operators';
import { OiqClientApplicationHistoryTable } from '../application-history/application-history.list';

@Component({
  selector: 'oiq-client-search-history',
  host: {
    class: 'oiq-client-search-history',
  },
  templateUrl: './client-search-history.component.html',
  styleUrls: ['./client-search-history.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [
    AsyncPipe,
    NgClass,
    FormsModule,
    ReactiveFormsModule,
    OiqClientApplicationHistoryTable,
    OiqFormInputComponent,
    OiqUiPanelComponent,
    OiqClientFundingHistoryTable,
  ],
})
export class OiqClientSearchHistoryComponent {
  public event = output<{ action: string; data?: any }>();
  public whiteLabelId = input<number | null | undefined>(null);
  public canCreate = input<boolean>(true);
  public showHistory = input<boolean>(true);
  private searchService = inject(SearchService);

  nameInputRef = viewChild<OiqFormInputComponent>('nameInputRef');
  nameInputCtrl = new FormControl();

  enableSelectApplication = input<boolean>(true);
  enableSelectFunding = input<boolean>(true);
  enableSelectClient = input<boolean>(true);
  enableMenuOptions = input<boolean>(true);
  enableButtonOptions = input<boolean>(false);

  // Form and state management
  expandedElements = signal<{ [clientId: number]: boolean }>({});
  searching = signal(false);

  applicationType = signal<string | null>(null);
  fundingType = signal<string | null>(null);

  clientResults$ = this.nameInputCtrl.valueChanges.pipe(
    tap(val => {
      this.event.emit({ action: 'search', data: val });
    }),
    debounceTime(400),
    distinctUntilChanged(),
    switchMap(val => {
      if (!val) {
        return of([]);
      }
      this.searching.set(true);
      return this.searchService
        .performClientsTypeahead(encodeURIComponent(val), null, this.whiteLabelId())
        .pipe(catchError(() => of([])));
    }),
    tap(() => this.searching.set(false)),
    map(data =>
      data.map(client => ({
        id: client.id,
        name: client.name,
        iso: client.iso,
        isoName: client.isoName,
        date: client.createdDate ? new Date(client.createdDate).toLocaleString() : 'N/A',
        createdDate: client.createdDate,
        expanded: false,
      }))
    )
  );

  constructor() {
    effect(() => {
      const _nameInputRef = this.nameInputRef();
      if (!_nameInputRef) {
        return;
      }
      _nameInputRef.focusIn();
    });
  }

  handlePanelEvent(event: any, client: any): void {
    switch (event.type) {
      case 'close':
      case 'open':
        if (!this.showHistory()) {
          return;
        }

        this.expandedElements.update(_old => ({ ..._old, [client.id]: !_old[client.id] }));
        break;
      default:
        break;
    }
  }

  handleHistoryEvent(
    client: any,
    event: { action: string; application?: any; funding?: any }
  ): void {
    this.event.emit({
      action: event.action,
      data: { client, application: event.application, funding: event.funding },
    });
  }
}
